// src/components/CompanySearchTypeahead.jsx
//
// The dashboard search bar. Behaves exactly as before for the table filter
// (value/onChange), and additionally offers live Companies House suggestions
// for every registered UK company in a dropdown. Read-only: selecting a result
// opens a view-only company profile, never enrichment (see usePlanAccess).
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import apiService from "../services/api";

const MIN_CHARS = 2;
const DEBOUNCE_MS = 350;
const MAX_SUGGESTIONS = 8;
const CACHE_TTL_MS = 5 * 60 * 1000;

export const SEARCH_TYPEAHEAD_STYLES = `
  .dash-sidebar:focus-within { z-index: 40; } /* let the dropdown float above the table */
  .ta-panel { position: absolute; left: 0; top: calc(100% + 6px); z-index: 60; width: 400px; max-width: 92vw; background: var(--surface); border: 1px solid var(--border-strong); border-radius: var(--radius); box-shadow: var(--shadow-lg); overflow: hidden; }
  .ta-head { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; border-bottom: 1px solid var(--border); font-size: var(--text-2xs); font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-faint); }
  .ta-list { list-style: none; max-height: 360px; overflow-y: auto; }
  .ta-item { display: block; width: 100%; text-align: left; padding: 9px 12px; background: none; border: none; border-bottom: 1px solid var(--border); cursor: pointer; font-family: var(--sans); color: var(--text); }
  .ta-item:last-child { border-bottom: none; }
  .ta-item[aria-selected="true"], .ta-item:hover { background: var(--row-hover); }
  .ta-row1 { display: flex; align-items: center; gap: 8px; }
  .ta-name { font-size: var(--text-sm); font-weight: 600; letter-spacing: -0.01em; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; flex: 1; }
  .ta-row2 { display: flex; align-items: center; gap: 8px; margin-top: 3px; font-size: var(--text-xs); color: var(--text-muted); }
  .ta-num { font-family: var(--mono); font-size: 10.5px; color: var(--text-faint); letter-spacing: 0.04em; flex-shrink: 0; }
  .ta-addr { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
  .ta-pill { display: inline-flex; align-items: center; padding: 1px 7px; border-radius: 100px; font-size: 9.5px; font-weight: 600; letter-spacing: 0.04em; text-transform: capitalize; border: 1px solid var(--border); color: var(--text-muted); background: var(--surface-raised); flex-shrink: 0; }
  .ta-pill.active { color: var(--green); background: var(--green-dim); border-color: rgba(10,124,92,0.2); }
  .ta-pill.warn { color: var(--amber); background: var(--amber-dim); border-color: rgba(146,88,0,0.2); }
  .ta-pill.bad { color: var(--red); background: var(--red-dim); border-color: rgba(191,41,41,0.2); }
  .ta-owned { display: inline-flex; padding: 1px 7px; border-radius: 100px; font-size: 9.5px; font-weight: 600; letter-spacing: 0.04em; color: var(--accent-mid); background: var(--accent-dim); border: 1px solid rgba(26,79,168,0.15); flex-shrink: 0; }
  .ta-msg { padding: 14px 12px; font-size: var(--text-sm); color: var(--text-muted); display: flex; align-items: center; gap: 8px; }
  .ta-foot { padding: 7px 12px; border-top: 1px solid var(--border); font-size: var(--text-2xs); color: var(--text-faint); background: var(--surface-raised); }
  .ta-spin { animation: ta-rot 0.8s linear infinite; }
  @keyframes ta-rot { to { transform: rotate(360deg); } }
  .dash-root.dark .ta-panel { background: var(--surface); }
`;

export function statusTone(status) {
  const s = (status || "").toLowerCase();
  if (s === "active") return "active";
  if (
    s.includes("dissolved") ||
    s.includes("liquidation") ||
    s.includes("receivership") ||
    s.includes("administration") ||
    s.includes("insolvency")
  )
    return "bad";
  if (s.includes("dormant") || s.includes("strike") || s.includes("closed"))
    return "warn";
  return "";
}

const year = (d) => (d ? new Date(d).getFullYear() : null);

// Small module-level cache so retyping / backspacing doesn't re-hit Companies
// House (which is rate-limited platform-wide).
const cache = new Map();

export default function CompanySearchTypeahead({
  value,
  onChange,
  onSelectResult, // (result) => void, result.ownedCompanyId set if it's already a lead
  canSearchRegistry = true,
  placeholder = "Company name or number…",
}) {
  const listId = useId();
  const wrapRef = useRef(null);
  const abortRef = useRef(null);
  const dirtyRef = useRef(false); // only fetch after the user actually typed
  const [open, setOpen] = useState(false);
  const [state, setState] = useState("idle"); // idle | loading | ready | error
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(-1);

  const query = (value || "").trim();
  const enabled = canSearchRegistry && query.length >= MIN_CHARS;

  // Debounced live lookup with cancellation of stale requests.
  useEffect(() => {
    if (!dirtyRef.current || !enabled) {
      setState("idle");
      return undefined;
    }
    const key = query.toLowerCase();
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) {
      setResults(hit.data);
      setState("ready");
      setActive(-1);
      return undefined;
    }

    setState("loading");
    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const res = await apiService.searchCompaniesHouse(query, {
          limit: MAX_SUGGESTIONS,
          signal: ctrl.signal,
        });
        const data = res?.data || [];
        cache.set(key, { at: Date.now(), data });
        setResults(data);
        setState("ready");
        setActive(-1);
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError")
          return;
        setResults([]);
        setState("error");
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, enabled]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Close on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const choose = useCallback(
    (r) => {
      setOpen(false);
      setActive(-1);
      onSelectResult?.(r);
    },
    [onSelectResult],
  );

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open || state !== "ready" || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault(); // don't submit the filter form, open the result
      choose(results[active]);
    }
  };

  const showPanel = open && enabled && dirtyRef.current;

  return (
    <div className="search-wrap" ref={wrapRef}>
      <input
        type="text"
        role="combobox"
        aria-expanded={showPanel}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        autoComplete="off"
        value={value}
        onChange={(e) => {
          dirtyRef.current = true;
          setOpen(true);
          onChange(e.target.value);
        }}
        onFocus={() => dirtyRef.current && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="field-input"
        style={{ paddingRight: value ? 30 : undefined }}
      />
      {value && (
        <button
          type="button"
          className="search-clear"
          onClick={() => {
            dirtyRef.current = false;
            setOpen(false);
            onChange("");
          }}
          title="Clear"
          aria-label="Clear search"
        >
          <X size={12} />
        </button>
      )}

      {showPanel && (
        <div className="ta-panel">
          <div className="ta-head">
            <span>Companies House</span>
            {state === "loading" && <Loader2 size={12} className="ta-spin" />}
          </div>

          {state === "loading" && results.length === 0 && (
            <div className="ta-msg">Searching all registered UK companies…</div>
          )}
          {state === "error" && (
            <div className="ta-msg">
              Companies House search is unavailable right now. The table below
              still filters your leads.
            </div>
          )}
          {state === "ready" && results.length === 0 && (
            <div className="ta-msg">
              No registered company matches “{query}”.
            </div>
          )}

          {results.length > 0 && (
            <ul
              className="ta-list"
              id={listId}
              role="listbox"
              aria-label="Companies House results"
            >
              {results.map((r, i) => (
                <li key={r.companyNumber} role="presentation">
                  <button
                    type="button"
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={i === active}
                    className="ta-item"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => choose(r)}
                  >
                    <div className="ta-row1">
                      <span className="ta-name">{r.companyName}</span>
                      {r.ownedCompanyId && (
                        <span className="ta-owned">In your leads</span>
                      )}
                      {r.companyStatus && (
                        <span
                          className={`ta-pill ${statusTone(r.companyStatus)}`}
                        >
                          {r.companyStatus.replace(/-/g, " ")}
                        </span>
                      )}
                    </div>
                    <div className="ta-row2">
                      <span className="ta-num">{r.companyNumber}</span>
                      {year(r.incorporatedAt) && (
                        <span>Inc. {year(r.incorporatedAt)}</span>
                      )}
                      {r.addressSnippet && (
                        <span className="ta-addr">{r.addressSnippet}</span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="ta-foot">Live from Companies House · view-only</div>
        </div>
      )}
    </div>
  );
}
